import { churchTime } from '@/utils/date';

// My BF Fulfilment from 2026 on: giving-api's GET /bf/fulfilment view turned into
// what the fulfilment frames draw (F1/F1b five months, F2 five months with a sow, F3
// sow, F4 one-time). giving-api decides which gift counts towards what — gifts fill
// the pledge first, the rest is sow — so nothing is allocated or summed here.

/** "2026-10" → "Oct 2026". */
export const formatPeriod = period => (period ? churchTime(`${period}-01`).format('MMM YYYY') : '');

/** "2026-10-05" (already a Singapore date from giving-api) → "05 Oct 2026". */
export const formatGiftDate = date => (date ? churchTime(date).format('DD MMM YYYY') : '');

const giftRows = gifts =>
  (gifts ?? []).map(gift => ({ label: formatGiftDate(gift.date), amount: gift.amount }));

/**
 * What the page draws for one year:
 * - `ring`: what was given towards the pledge, over the pledge total. Beside a sow
 *   (F2, five months or one-time) `sown` is what went to the sow, drawn under it;
 *   otherwise it is null. A sow alone (F3) has no ring until its first gift, then a
 *   full green one with leaves (`sow: true`) carrying what was sown: a sow has no
 *   target to fill.
 * - `cards`, in the frames' order. A section's `info` names the explainer its ⓘ
 *   opens ('fulfilled' or 'sown'); without one it has no ⓘ. Giving with no pledge
 *   gets no card: only members with a pledge for the year reach the page. A pledge
 *   card — five months or one-time, with or without a sow — carries the pledge's
 *   own percentage; a sow alone has none.
 */
export function fulfilmentCards(view) {
  const pledge = view?.pledge ?? null;
  const fulfilment = view?.fulfilment ?? {};
  const pledged = fulfilment.pledge ?? { amount: 0, percentage: 0, gifts: [] };
  const sow = fulfilment.sow ?? { amount: 0, gifts: [] };
  const hasSow = Boolean(view?.sow);
  const sown = sow.amount > 0;
  const cards = [];
  let ring = null;
  // The ring is the pledge alone, five months or one-time; beside a sow it also
  // carries what was sown.
  const pledgeRing = () => ({
    total: pledge.total,
    fulfilled: pledged.amount,
    sown: hasSow ? sow.amount : null,
  });

  if (pledge?.oneTime > 0) {
    // F4: one row carrying the pledged amount that opens onto the gifts, as the
    // frame draws it. The pledge total is that row, so nothing repeats it under
    // the ring.
    ring = pledgeRing();
    cards.push({
      key: 'one-time',
      tone: 'given',
      title: 'Building Fund',
      sections: [{ title: 'Amount pledged', total: pledge.total, rows: giftRows(pledged.gifts) }],
      percentage: pledged.percentage,
    });
  } else if (pledge?.total > 0) {
    // F1/F1b, or F2 with a sow.
    ring = pledgeRing();
    cards.push({
      key: 'months',
      tone: 'months',
      title: '5-month pledge',
      sections: [
        {
          title: 'Amount pledged',
          total: pledge.total,
          rows: (pledge.months ?? []).map(month => ({
            label: formatPeriod(month.period),
            amount: month.amount,
          })),
        },
        // Beside a sow (F2) it carries an ⓘ, as the sow row does.
        {
          title: 'Amount fulfilled',
          total: pledged.amount,
          rows: giftRows(pledged.gifts),
          info: hasSow ? 'fulfilled' : undefined,
        },
      ],
      percentage: pledged.percentage,
    });
  }

  if (hasSow) {
    // F3, and under the pledge card on F2. With a pledge the row is always there —
    // "$ -" until something is sown — and carries the explainer, since gifts fill the
    // pledge first. A sow alone has no row until its first gift, which also draws its
    // ring, and no explainer.
    const withPledge = cards.length > 0;
    if (sown && !withPledge) {
      ring = { total: sow.amount, fulfilled: sow.amount, sown: null, sow: true };
    }
    cards.push({
      key: 'sow',
      tone: 'sow',
      title: 'Sow',
      note: view.sow.text,
      sections:
        withPledge || sown
          ? [
              {
                title: 'Amount sown',
                total: sown ? sow.amount : null,
                rows: giftRows(sow.gifts),
                info: withPledge ? 'sown' : undefined,
              },
            ]
          : [],
    });
  }

  return { ring, cards };
}
